import {
    ConfigurationResult,
    ReservationGroupParameters,
    ReservationGroupResult,
    ServiceAvailabilityParameters,
    ServiceAvailabilityResult,
} from '@mews-distributor/data-access';
import { CaseStyle, PlatformEnvironment } from '@mews/enums';
import { getBrowserInfo, sendRequest, SendRequestOptions } from '@mews/framework';
import { createUUID } from '@mews/std';
import type { ReservationPriceParameters } from '@mews-distributor/data-access';
import { createSession } from '../../utils/session/session';
import {
    AVAILABILITY_BLOCKS_GET_ALL,
    CONFIGURATION_GET,
    CONFIGURATIONS_GET,
    ENTERPRISES_GET_PAYMENT_CONFIGURATION,
    EXCHANGE_RATES_GET_ALL,
    PAYMENT_CARDS_AUTHORIZE,
    PRODUCTS_GET_PRICES,
    PROMOTED_SERVICES_GET,
    RESERVATION_GROUPS_CREATE,
    RESERVATION_GROUPS_GET,
    RESERVATIONS_GET_PRICING,
    RESERVATIONS_PRICE,
    SERVICE_DOCUMENTS_GET_ALL,
    SERVICES_GET_AVAILABILITY,
    SERVICES_GET_PRICING,
} from '../endpoints';
import { getRequestCredentials } from './credentials';

interface ApiServiceOptions {
    environment: PlatformEnvironment | string;
    getDistributorBaseUrl: () => string;
    getGeneralBaseUrl: () => string;
    getClientName: () => string;
    getSessionToken: () => string;
}
type RequestData = object | null;

export const createApiService = ({
    environment,
    getDistributorBaseUrl,
    getGeneralBaseUrl,
    getClientName,
    getSessionToken,
}: ApiServiceOptions) => {
    const request = async (
        baseUrl: string,
        apiUrl: string,
        data: RequestData = {},
        options: SendRequestOptions = {}
    ) => {
        const absoluteUrl = `${baseUrl}${apiUrl}`;
        const extendedData = appendMetadata(data);
        const correlationId = createUUID();

        try {
            return await sendRequest(
                absoluteUrl,
                {
                    credentials: getRequestCredentials(environment),
                    customHeaders: {
                        ...options.customHeaders,
                        'X-Accept-Casing': CaseStyle.Camel,
                        'X-Mews-CorrelationId': correlationId,
                    },
                    ...options,
                },
                extendedData
            );
        } catch (error) {
            error.correlationId = correlationId;
            throw error;
        }
    };

    const distributorRequest = async (apiUrl: string, data: RequestData, options?: SendRequestOptions) => {
        const baseUrl = getDistributorBaseUrl();
        return request(baseUrl, apiUrl, data, options);
    };

    const generalRequest = async (apiUrl: string, data: RequestData, options: SendRequestOptions) => {
        const baseUrl = getGeneralBaseUrl();
        return request(baseUrl, apiUrl, data, options);
    };

    const appendMetadata = (data: RequestData) => {
        const client = getClientName();
        const sessionToken = getSessionToken();
        const session = createSession({ sessionToken, clientName: client });

        return { ...data, client, session };
    };
    interface GetConfigurationOptions {
        ids: string[];
        primaryId: string;
    }
    const getConfiguration = async ({ ids, primaryId }: GetConfigurationOptions) =>
        distributorRequest(CONFIGURATION_GET, { ids, primaryId });
    const getConfigurations = async ({ ids, primaryId }: GetConfigurationOptions): Promise<ConfigurationResult> =>
        distributorRequest(CONFIGURATIONS_GET, { ids, primaryId });

    const getServicesAvailability = async ({
        serviceId,
        bookingEngineId,
        availabilityBlockId,
        startUtc,
        endUtc,
        enterpriseId,
        fullAmounts,
        languageCode,
    }: ServiceAvailabilityParameters): Promise<ServiceAvailabilityResult> =>
        distributorRequest(SERVICES_GET_AVAILABILITY, {
            serviceId,
            bookingEngineId,
            availabilityBlockId,
            startUtc,
            endUtc,
            enterpriseId,
            fullAmounts,
            languageCode,
        });

    const getServicesPricing = async ({
        serviceId,
        configurationId,
        availabilityBlockId,
        categoryIds,
        productIds,
        occupancyData,
        currencyCode,
        startUtc,
        endUtc,
        voucherCode,
        enterpriseId,
        fullAmounts,
        languageCode,
    }: $TSFixMe) =>
        distributorRequest(SERVICES_GET_PRICING, {
            serviceId,
            bookingEngineId: configurationId,
            availabilityBlockId,
            categoryIds,
            productIds,
            occupancyData,
            currencyCode,
            startUtc,
            endUtc,
            voucherCode,
            enterpriseId,
            fullAmounts,
            languageCode,
        });

    const getReservationPricing = async ({
        bookingEngineId,
        hotelId,
        languageCode,
        startUtc,
        endUtc,
        voucherCode,
        roomCategoryId,
        productIds,
        occupancies,
        currencyCodes,
        rateIds,
        availabilityBlockId,
    }: $TSFixMe) =>
        distributorRequest(RESERVATIONS_GET_PRICING, {
            hotelId,
            bookingEngineId,
            languageCode,
            startUtc,
            endUtc,
            voucherCode,
            roomCategoryId,
            productIds,
            occupancies,
            currencyCodes,
            rateIds,
            availabilityBlockId,
        });

    const getReservationsPrice = async ({
        reservations,
        promotedServiceReservations,
        configurationId,
        languageCode,
        currencyCode,
        settlementCurrencyCode,
    }: ReservationPriceParameters) =>
        distributorRequest(RESERVATIONS_PRICE, {
            reservations,
            promotedServiceReservations,
            configurationId,
            languageCode,
            currencyCode,
            settlementCurrencyCode,
        });

    const createReservationGroup = async ({
        configurationId,
        creditCardData,
        currencyCodes,
        customer,
        hotelId,
        languageCode,
        reservations,
        promotedServiceReservations,
        booker,
        availabilityBlockId,
        recaptchaVerificationToken,
    }: $TSFixMe) =>
        distributorRequest(RESERVATION_GROUPS_CREATE, {
            configurationId,
            creditCardData,
            currencyCodes,
            customer,
            hotelId,
            languageCode,
            reservations,
            promotedServiceReservations,
            booker,
            availabilityBlockId,
            recaptchaVerificationToken,
        });

    const authorizePaymentCard = async ({ enterpriseId, paymentCardId }: $TSFixMe) =>
        distributorRequest(PAYMENT_CARDS_AUTHORIZE, { enterpriseId, paymentCardId, browserInfo: getBrowserInfo() });

    const getPaymentConfiguration = async ({ enterpriseId, languageCode }: $TSFixMe) =>
        distributorRequest(ENTERPRISES_GET_PAYMENT_CONFIGURATION, { enterpriseId, languageCode });

    const getProductsPrices = async ({
        endUtc,
        startUtc,
        occupancyData,
        currencyCode,
        configurationId,
        productIds,
    }: $TSFixMe) =>
        distributorRequest(PRODUCTS_GET_PRICES, {
            endUtc,
            startUtc,
            occupancyData,
            currencyCode,
            configurationId,
            productIds,
        });

    const getReservationGroup = async ({ hotelId, ids }: ReservationGroupParameters): Promise<ReservationGroupResult> =>
        distributorRequest(RESERVATION_GROUPS_GET, {
            hotelId,
            ids,
            extent: {
                paymentRequests: true,
                payments: true,
                resourceCategories: true,
            },
        });

    const getExchangeRates = async ({ enterpriseId, sourceCurrencyCode }: $TSFixMe) =>
        distributorRequest(EXCHANGE_RATES_GET_ALL, { sourceCurrencyCode, enterpriseId });

    const getPaymentCards = async ({ paymentCardIds }: $TSFixMe) =>
        distributorRequest('/paymentCards/getAll', { paymentCardIds });
    const getAvailabilityBlocks = async ({ availabilityBlockIds, enterpriseId }: $TSFixMe) =>
        distributorRequest(AVAILABILITY_BLOCKS_GET_ALL, { availabilityBlockIds, enterpriseId });

    const getServiceDocuments = async ({ serviceIds, types }: $TSFixMe) =>
        // @ts-expect-error $TSFixMeError: Expected 3 arguments, but got 2.
        generalRequest(SERVICE_DOCUMENTS_GET_ALL, {
            serviceIds,
            types,
            extent: {
                markdownValues: true,
            },
        });

    const getPromotedServices = async ({ endUtc, startUtc, currencyCode, languageCode, serviceId }: $TSFixMe) =>
        distributorRequest(PROMOTED_SERVICES_GET, {
            serviceId,
            startUtc,
            endUtc,
            currencyCode,
            languageCode,
        });

    return {
        authorizePaymentCard,
        createReservationGroup,
        getServicesAvailability,
        getAvailabilityBlocks,
        getConfiguration,
        getConfigurations,
        getExchangeRates,
        getPaymentCards,
        getPaymentConfiguration,
        getServicesPricing,
        getProductsPrices,
        getPromotedServices,
        getReservationGroup,
        getReservationPricing,
        getReservationsPrice,
        getServiceDocuments,
    };
};
